import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { fileAccessRestrictedHref } from '../storageAccess/fileAccessRestrictedUrls';
import { DOCUMENT_HOST_OPEN_TIMEOUT_MS } from '../storageAccess/documentHostOpenFailed';
import {
  FileReviewWorkspace,
  type FileReviewPanelPlacement,
  enableFileReviewDiscussionPlacement,
  findAnchorById,
  readFileReviewPanelOpen,
  useAutoHideDiscussionPanel,
  useDiscussionAnchorFromUrl,
  useFileReviewPanelPlacement,
  useFileReviewPanelWidth,
  useFileReviewThread,
  writeFileReviewPanelOpen,
  writeFileReviewPanelPlacement
} from '@jarvis/platform-file-review';
import '@jarvis/platform-file-review/styles.css';
import { bootstrapFileReviewApi } from '../fileReview/bootstrapFileReviewApi';
import { usePdfViewerIdentity } from '../pdfViewer/usePdfViewerIdentity';
import { showConfirmDialog } from '../dialogs/confirmDialog';
import { showToast } from '../notifications/toast';
import { DiscussionNotificationPrompt } from '../notifications/DiscussionNotificationPrompt';
import { useDiscussionMessageNotifications } from '../notifications/useDiscussionMessageNotifications';
import { useMediaFileMetadata } from '../mediaViewer/mediaViewerMetadata';
import {
  formatMediaDateModified,
  formatMediaFileSize
} from '../mediaViewer/mediaViewerFormatters';
import { documentHostSrc, resolveDxTheme } from '../documentEditor/documentEditorUrls';
import { syncThemeToFrame } from '../theme/themeManager';
import type { DocumentEditorMode } from '../documentEditor/documentEditorUrls';
import { shareDocumentLink } from '../documentEditor/documentViewerActions';
import {
  readSpreadsheetCellLinksVisible,
  writeSpreadsheetCellLinksVisible
} from './spreadsheetEditorUiPrefs';
import { DocumentOpenPickerPopup } from '../documentEditor/DocumentOpenPickerPopup';
import { useDocumentEditorOpenFlow } from '../documentEditor/useDocumentEditorOpenFlow';
import type { SpreadsheetSelectionState } from './spreadsheetBridgeTypes';
import { useSpreadsheetHostBridge } from './useSpreadsheetHostBridge';
import { SpreadsheetCommentPopover } from './SpreadsheetCommentPopover';
import { ActiveUsersBar } from '../realtime/ActiveUsersBar';
import { AutoSaveIndicator } from '../realtime/AutoSaveIndicator';
import { useDocumentPresence, peerEditLockToastMessage } from '../realtime/useDocumentPresence';
import { useDocumentEditingSignals } from '../realtime/useDocumentEditingSignals';
import { useDocumentFileSavedHandler } from '../editorPreferences/useDocumentFileSavedHandler';
import { MediaViewerTitle } from '../mediaViewer/MediaViewerTitle';
import '../mediaViewer/MediaViewerTitle.css';
import { EditorCollaborationReloadOverlay } from '../realtime/EditorCollaborationReloadOverlay';
import '../realtime/EditorCollaborationReload.css';
import '../realtime/EditorHeader.css';
import { isFileReviewDisabledStorageMount } from '../storageFileAudit/formatters';
import {
  isDocumentPresenceEnabled,
  shouldShowDocumentViewOnlyBadge
} from '../documentEditor/documentEditorCollaboration';
import { useShellLayout } from '../shell/useShellLayout';
import './SpreadsheetEditorShell.css';

bootstrapFileReviewApi();

export interface SpreadsheetEditorShellProps {
  mode: DocumentEditorMode;
  mountId: string;
  path: string;
  title: string;
  readOnly?: boolean;
  discussionPlacement?: FileReviewPanelPlacement;
  /** Hide title row when nested inside the file-browser preview popup. */
  embeddedInPopup?: boolean;
}

export function SpreadsheetEditorShell({
  mode,
  mountId,
  path,
  title,
  readOnly = false,
  discussionPlacement: discussionPlacementProp,
  embeddedInPopup = false
}: SpreadsheetEditorShellProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isNarrow } = useShellLayout();
  const discussionAnchorParam = (searchParams.get('discussionAnchor') ?? '').trim();
  const discussionPlacement = useFileReviewPanelPlacement('spreadsheet');
  const discussionsEnabled = !isFileReviewDisabledStorageMount(mountId) && !isNarrow;
  const placement = discussionsEnabled
    ? (discussionPlacementProp ?? discussionPlacement)
    : 'off';
  const autoHideDiscussion = useAutoHideDiscussionPanel();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const navigatedToRestrictedRef = useRef(false);
  const [chatOpen, setChatOpen] = useState(() => readFileReviewPanelOpen('spreadsheet'));
  const [showCellLinks, setShowCellLinks] = useState(readSpreadsheetCellLinksVisible);
  const [activeAnchorId, setActiveAnchorId] = useState<string | null>(null);
  const [commentPopoverSelection, setCommentPopoverSelection] =
    useState<SpreadsheetSelectionState | null>(null);
  const [newerVersionBy, setNewerVersionBy] = useState<string | null>(null);
  const [peerReloading, setPeerReloading] = useState(false);

  const goToAccessRestricted = useCallback(() => {
    if (navigatedToRestrictedRef.current) return;
    navigatedToRestrictedRef.current = true;
    navigate(fileAccessRestrictedHref(), { replace: true });
  }, [navigate]);

  const src = useMemo(
    () => documentHostSrc('spreadsheet', mode, mountId, path, resolveDxTheme(), discussionsEnabled),
    [mode, mountId, path, discussionsEnabled]
  );

  const metadata = useMediaFileMetadata(mountId, path, true);
  const { userId, userName, hasPhoto, userAliases } = usePdfViewerIdentity(true);
  const { panelStyle, startResize, resetWidth } = useFileReviewPanelWidth(
    workspaceRef,
    placement,
    'spreadsheet'
  );

  const onReviewError = useCallback((message: string) => showToast(message, 'error'), []);

  const openDiscussionPanel = useCallback(() => {
    if (!discussionsEnabled) return;
    if (placement === 'off') enableFileReviewDiscussionPlacement('spreadsheet');
    setChatOpen(true);
    writeFileReviewPanelOpen(true, 'spreadsheet');
  }, [discussionsEnabled, placement]);

  const { onPeerActivity } = useDiscussionMessageNotifications({
    enabled: discussionsEnabled && Boolean(mountId && path && userId),
    onOpenDiscussionPanel: openDiscussionPanel
  });

  const review = useFileReviewThread(mountId, path, discussionsEnabled, {
    commentsPanelOpen: discussionsEnabled && placement !== 'off',
    onError: onReviewError,
    currentUserId: userId,
    currentUserAliases: userAliases,
    onPeerActivity
  });

  const toggleChat = useCallback(() => {
    if (!discussionsEnabled) return;
    if (placement === 'off') {
      enableFileReviewDiscussionPlacement('spreadsheet');
      setChatOpen(true);
      writeFileReviewPanelOpen(true, 'spreadsheet');
      return;
    }
    setChatOpen((open) => {
      const next = !open;
      writeFileReviewPanelOpen(next, 'spreadsheet');
      return next;
    });
  }, [discussionsEnabled, placement]);

  useDiscussionAnchorFromUrl({
    discussionAnchorId: discussionsEnabled ? discussionAnchorParam || null : null,
    onAnchorFromUrl: setActiveAnchorId,
    onOpenDiscussion: openDiscussionPanel
  });

  const hostMenuActionsRef = useRef<{
    commentOnSelection?: (selection?: SpreadsheetSelectionState | null) => void;
    toggleCellLinks?: () => void;
    toggleChat?: () => void;
    share?: () => void;
    open?: (source: 'pc' | 'storage') => void;
    setDiscussionPlacement?: (placement: FileReviewPanelPlacement) => void;
  }>({});
  const bridgeSelectionRef = useRef<SpreadsheetSelectionState | null>(null);

  const {
    ready: bridgeReady,
    editorSessionId,
    selection: bridgeSelection,
    autoSave,
    navigateToAnchor,
    cancelNavigation,
    requestForceReload,
    applyCollaborationSaveTime,
    requestDirtyState,
    requestAutoSaveNow,
    setDiscussionActive,
    setCellLinksState,
    setDiscussionState,
    setDiscussionPlacementState,
    setPeerEditLocked
  } = useSpreadsheetHostBridge(iframeRef, true, {
    onNavigationFailed: (rangeLabel) => {
      showToast(
        rangeLabel
          ? `Could not scroll to ${rangeLabel}. Try clicking the sheet tab, then jump again.`
          : 'Could not scroll to that cell. Try clicking the sheet tab, then jump again.',
        'warning'
      );
    },
    onOpenFailed: () => goToAccessRestricted(),
    onRequestCommentOnSelection: (selection) =>
      hostMenuActionsRef.current.commentOnSelection?.(selection),
    onRequestToggleCellLinks: () => hostMenuActionsRef.current.toggleCellLinks?.(),
    onRequestToggleDiscussion: () => hostMenuActionsRef.current.toggleChat?.(),
    onRequestShare: () => hostMenuActionsRef.current.share?.(),
    onRequestOpen: (source) => hostMenuActionsRef.current.open?.(source),
    onRequestSetDiscussionPlacement: (next) =>
      hostMenuActionsRef.current.setDiscussionPlacement?.(next)
  });

  const {
    pickerOpen,
    setPickerOpen,
    fileInputRef,
    fileInputAccept,
    handleOpenRequest,
    onFileInputChange,
    onPickerOpen
  } = useDocumentEditorOpenFlow({
    kind: 'spreadsheet',
    mountId,
    path,
    requestDirtyState
  });

  useEffect(() => {
    navigatedToRestrictedRef.current = false;
  }, [mountId, path]);

  useEffect(() => {
    // Popup previews should not navigate the whole SPA away on a slow open.
    if (embeddedInPopup || bridgeReady || peerReloading) return;
    const timer = window.setTimeout(() => goToAccessRestricted(), DOCUMENT_HOST_OPEN_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [embeddedInPopup, bridgeReady, peerReloading, goToAccessRestricted, mountId, path]);

  const onDiscussionPlacementChange = useCallback(
    (next: FileReviewPanelPlacement) => {
      if (!discussionsEnabled) return;
      const open = next !== 'off';
      writeFileReviewPanelPlacement(next, 'spreadsheet');
      writeFileReviewPanelOpen(open, 'spreadsheet');
      setChatOpen(open);
      if (!open) cancelNavigation();
    },
    [cancelNavigation, discussionsEnabled]
  );

  const toggleCellLinks = useCallback(() => {
    setShowCellLinks((visible) => {
      const next = !visible;
      if (!next) cancelNavigation();
      writeSpreadsheetCellLinksVisible(next);
      showToast(
        next
          ? 'Cell links on — click a discussion message to jump to its cell'
          : 'Cell links off',
        'info'
      );
      return next;
    });
  }, [cancelNavigation]);

  const commentOnSelection = useCallback(
    (fromHost?: SpreadsheetSelectionState | null) => {
      if (!discussionsEnabled) return;
      const sel = fromHost?.ranges?.length ? fromHost : bridgeSelectionRef.current;
      if (!sel?.ranges?.length) {
        showToast('Select one or more cells first.', 'warning');
        return;
      }
      setCommentPopoverSelection(sel);
    },
    [discussionsEnabled]
  );

  const shareDocument = useCallback(() => {
    void shareDocumentLink('spreadsheet', mode === 'preview' ? 'preview' : 'edit', mountId, path);
  }, [mode, mountId, path]);

  bridgeSelectionRef.current = bridgeSelection;
  hostMenuActionsRef.current = {
    commentOnSelection,
    toggleCellLinks,
    toggleChat,
    share: shareDocument,
    open: handleOpenRequest,
    setDiscussionPlacement: onDiscussionPlacementChange
  };

  useEffect(() => {
    setActiveAnchorId(null);
    setCommentPopoverSelection(null);
  }, [mountId, path]);

  useEffect(() => {
    if (!bridgeReady) return;
    setDiscussionActive(
      discussionsEnabled && ((chatOpen && placement !== 'off') || commentPopoverSelection != null)
    );
  }, [
    bridgeReady,
    chatOpen,
    commentPopoverSelection,
    discussionsEnabled,
    placement,
    setDiscussionActive
  ]);

  useEffect(() => {
    if (!bridgeReady) return;
    setCellLinksState(showCellLinks);
  }, [bridgeReady, showCellLinks, setCellLinksState]);

  useEffect(() => {
    if (!bridgeReady) return;
    setDiscussionState(discussionsEnabled && chatOpen);
  }, [bridgeReady, chatOpen, discussionsEnabled, setDiscussionState]);

  useEffect(() => {
    if (!bridgeReady) return;
    setDiscussionPlacementState(placement);
  }, [bridgeReady, placement, setDiscussionPlacementState]);

  const cancelCommentPopover = useCallback(() => {
    setCommentPopoverSelection(null);
  }, []);

  const saveCommentPopover = useCallback(
    async (comment: string) => {
      const sel = commentPopoverSelection;
      if (!sel?.ranges?.length) return;
      const anchorId = await review.commitSpreadsheetAnchor({
        sheetIndex: sel.sheetIndex,
        sheetName: sel.sheetName,
        ranges: sel.ranges,
        selectedText: sel.rangeLabel || sel.selectedText,
        comment
      });
      setCommentPopoverSelection(null);
      if (anchorId) {
        setActiveAnchorId(anchorId);
        openDiscussionPanel();
        if (showCellLinks && bridgeReady) {
          navigateToAnchor({
            id: anchorId,
            kind: 'spreadsheet',
            sheetIndex: sel.sheetIndex,
            sheetName: sel.sheetName,
            ranges: sel.ranges,
            selectedText: sel.rangeLabel || sel.selectedText,
            createdBy: '',
            createdByDisplayName: null,
            sourceSite: '',
            createdAt: new Date().toISOString()
          });
        }
      }
    },
    [bridgeReady, commentPopoverSelection, navigateToAnchor, openDiscussionPanel, review, showCellLinks]
  );

  const onMessageEntered = useCallback(
    async (text: string) => {
      // Normal discussion messages are unpinned. Cell-linked comments are created only through the
      // explicit "comment on selection" flow; activeAnchorId is just for highlighting/jump state.
      await review.sendMessage(text, null);
    },
    [review]
  );

  const onMessageClick = useCallback(
    (anchorId: string | null) => {
      setActiveAnchorId(anchorId);
      if (!anchorId) return;
      if (!showCellLinks) {
        showToast('Turn on cell links to jump to cells in the sheet', 'info');
        return;
      }
      if (!bridgeReady) {
        showToast('Spreadsheet is still loading — try again in a moment', 'warning');
        return;
      }
      const anchor = findAnchorById(review.anchors, anchorId);
      if (!anchor || anchor.kind !== 'spreadsheet') {
        showToast('No cell range for this message', 'warning');
        return;
      }
      navigateToAnchor(anchor);
    },
    [bridgeReady, navigateToAnchor, review.anchors, showCellLinks]
  );

  const onMessageDeleted = useCallback(
    async (messageId: string) => {
      // Deleting the last message on a selection comment also removes its
      // anchor server-side (ITB-1649); drop the stale active-anchor reference.
      const deletedAnchorId = review.messages.find((m) => m.id === messageId)?.anchorId ?? null;
      await review.deleteMessage(messageId);
      if (deletedAnchorId) {
        setActiveAnchorId((current) => (current === deletedAnchorId ? null : current));
      }
    },
    [review]
  );

  const documentReadOnly = readOnly || mode === 'preview';
  const presenceEnabled = isDocumentPresenceEnabled({ mountId, embeddedInPopup });

  useEffect(() => {
    if (bridgeReady && peerReloading) setPeerReloading(false);
  }, [bridgeReady, peerReloading]);

  useEffect(() => {
    if (!peerReloading) return;
    const timer = window.setTimeout(() => setPeerReloading(false), 60000);
    return () => window.clearTimeout(timer);
  }, [peerReloading]);

  const peerEditLockedRef = useRef(false);
  const onFileSaved = useDocumentFileSavedHandler({
    userAliases,
    userDisplayName: userName,
    editorSessionId,
    requestDirtyState,
    requestForceReload,
    onCollaborationSaveApplied: applyCollaborationSaveTime,
    setNewerVersionBy,
    onPeerReloadStarting: () => setPeerReloading(true),
    isPeerEditLocked: () => peerEditLockedRef.current
  });

  const { roster, notifyEditing, notifyTyping, isEditLockHolder, isPeerEditLocked, editLockHolder } =
    useDocumentPresence({
      mountId,
      path,
      enabled: presenceEnabled,
      currentUserId: userId,
      currentUserAliases: userAliases,
      onFileSaved,
      onPeerEditLock: (holder) => showToast(peerEditLockToastMessage(holder), 'warning'),
      onEditLockDenied: (holder) => showToast(peerEditLockToastMessage(holder), 'warning')
    });
  peerEditLockedRef.current = isPeerEditLocked;

  const [stoppingEditing, setStoppingEditing] = useState(false);

  useEffect(() => {
    if (!bridgeReady || documentReadOnly) return;
    setPeerEditLocked(isPeerEditLocked);
  }, [bridgeReady, documentReadOnly, isPeerEditLocked, setPeerEditLocked]);

  useEffect(() => {
    if (!presenceEnabled || documentReadOnly) return;
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || !('type' in data)) return;
      if (data.type === 'jarvis-spreadsheet:peer-edit-blocked') {
        showToast(peerEditLockToastMessage(editLockHolder), 'warning');
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [documentReadOnly, editLockHolder, presenceEnabled]);

  const { isLocalTyping } = useDocumentEditingSignals({
    enabled: presenceEnabled && !documentReadOnly,
    iframeRef,
    editingChangedType: 'jarvis-spreadsheet:editing-changed',
    typingActivityType: 'jarvis-spreadsheet:typing-activity',
    notifyEditing,
    notifyTyping,
    peerEditLocked: isPeerEditLocked,
    onEditLockDeniedLocally: () => showToast(peerEditLockToastMessage(editLockHolder), 'warning')
  });

  const onStopEditing = useCallback(() => {
    if (documentReadOnly || stoppingEditing) return;
    setStoppingEditing(true);
    void (async () => {
      try {
        const dirty = await requestDirtyState();
        if (dirty) {
          const save = await requestAutoSaveNow();
          if (!save.ok) {
            const detail =
              save.reason === 'conflict'
                ? 'A newer version exists. Refresh it, then try again.'
                : 'Fix the save error, then try again.';
            showToast(`Could not stop editing. ${detail}`, 'error');
            return;
          }
        }
        const released = await notifyEditing(false);
        if (!released) {
          showToast('Could not release editing. Check your connection and try again.', 'error');
          return;
        }
        showToast(dirty ? 'Editing stopped. File saved.' : 'Editing stopped.', dirty ? 'success' : 'info');
      } finally {
        setStoppingEditing(false);
      }
    })();
  }, [documentReadOnly, notifyEditing, requestAutoSaveNow, requestDirtyState, stoppingEditing]);

  const onRefresh = useCallback(() => {
    void (async () => {
      const dirty = await requestDirtyState();
      if (dirty) {
        const ok = await showConfirmDialog({
          title: 'Discard unsaved changes?',
          message:
            'You have unsaved changes. Reloading the latest version will discard them. Continue?',
          confirmText: 'Reload',
          danger: true
        });
        if (!ok) return;
      }
      setNewerVersionBy(null);
      requestForceReload();
    })();
  }, [requestDirtyState, requestForceReload]);

  const annotateReadOnly = !discussionsEnabled || (!review.loading && !review.canAnnotate);
  const showViewOnlyBadge = shouldShowDocumentViewOnlyBadge({
    discussionsEnabled,
    annotateReadOnly,
    hasReviewError: Boolean(review.error)
  });

  return (
    <div
      className={
        embeddedInPopup
          ? 'spreadsheet-editor-shell spreadsheet-editor-shell--embedded'
          : 'spreadsheet-editor-shell'
      }
    >
      {embeddedInPopup ? null : (
      <div className="editor-header">
        <MediaViewerTitle
          kind="spreadsheet"
          title={title}
          path={path}
          placement={placement}
          discussionExpanded={chatOpen}
          onDiscussionToggle={discussionsEnabled ? toggleChat : undefined}
          className="editor-header__title"
        />
        <span className="editor-header__center">
          {presenceEnabled ? (
            <ActiveUsersBar
              roster={roster}
              currentUserId={userId}
              maxAvatars={5}
              localTypingActive={isLocalTyping}
            />
          ) : null}
        </span>
        <span className="editor-header__meta" aria-label="File details">
          <span className="editor-header__meta-item">
            <span className="editor-header__meta-label">Modified</span>
            <span className="editor-header__meta-value">
              {formatMediaDateModified(metadata?.dateModified)}
            </span>
          </span>
          <span className="editor-header__meta-sep" aria-hidden="true">
            ·
          </span>
          <span className="editor-header__meta-item">
            <span className="editor-header__meta-label">Size</span>
            <span className="editor-header__meta-value">{formatMediaFileSize(metadata?.size)}</span>
          </span>
          {!documentReadOnly ? (
            <AutoSaveIndicator
              status={autoSave.status}
              lastSavedAt={autoSave.lastSavedAt}
              reason={autoSave.reason}
              onRetry={requestAutoSaveNow}
            />
          ) : null}
          {isEditLockHolder && !documentReadOnly ? (
            <button
              type="button"
              className="editor-header__stop-editing"
              title="Save and release editing so others can edit"
              aria-label="Stop editing"
              disabled={stoppingEditing}
              onClick={onStopEditing}
            >
              {stoppingEditing ? 'Saving…' : 'Stop Editing'}
            </button>
          ) : null}
          {isPeerEditLocked && !documentReadOnly ? (
            <span className="editor-header__badge" title={peerEditLockToastMessage(editLockHolder)}>
              Locked
            </span>
          ) : null}
          <button
            type="button"
            className="editor-header__refresh"
            title="Reload latest from storage"
            aria-label="Reload latest from storage"
            onClick={onRefresh}
          >
            <span className="dx-icon dx-icon-refresh" aria-hidden="true" />
          </button>
          {showViewOnlyBadge ? <span className="editor-header__badge">View only</span> : null}
          {discussionsEnabled && review.error && review.messages.length === 0 ? (
            <span className="editor-header__badge editor-header__badge--error" role="alert">
              Discussion unavailable
            </span>
          ) : null}
        </span>
      </div>
      )}
      {newerVersionBy ? (
        <div className="editor-newer-banner" role="alert">
          <span className="editor-newer-banner__msg">
            A newer version was saved by {newerVersionBy}. Refresh to load it (your unsaved edits will
            be discarded).
          </span>
          <button type="button" className="editor-newer-banner__action" onClick={onRefresh}>
            Refresh
          </button>
          <button
            type="button"
            className="editor-newer-banner__dismiss"
            aria-label="Dismiss"
            title="Dismiss"
            onClick={() => setNewerVersionBy(null)}
          >
            ×
          </button>
        </div>
      ) : null}
      <FileReviewWorkspace
        workspaceRef={workspaceRef}
        placement={placement}
        panelOpen={discussionsEnabled && chatOpen}
        autoHide={autoHideDiscussion}
        viewerClassName="spreadsheet-editor-shell__viewer"
        panelProps={{
          messages: review.messages,
          currentUserId: userId,
          currentUserAliases: userAliases,
          currentUserName: userName,
          currentUserHasPhoto: hasPhoto,
          loading: review.loading,
          readOnly: annotateReadOnly,
          canAnnotate: review.canAnnotate,
          activeAnchorId,
          jumpLabel: 'Jump to cell',
          title: 'Discussion',
          panelStyle: chatOpen ? panelStyle : undefined,
          onResizeStart: startResize,
          onResizeReset: resetWidth,
          onMessageEntered,
          onMessageUpdated: review.updateMessage,
          onMessageDeleted,
          onMessageClick,
          onComposeValidationError: onReviewError,
          mountId,
          path
        }}
      >
        <iframe
          ref={iframeRef}
          className="spreadsheet-editor-shell__frame"
          src={src}
          title={title}
          onLoad={(event) => syncThemeToFrame(event.currentTarget.contentWindow)}
        />
        <EditorCollaborationReloadOverlay
          visible={!bridgeReady || peerReloading}
          label={peerReloading ? 'Loading latest version…' : 'Loading spreadsheet…'}
        />
        {discussionsEnabled && commentPopoverSelection ? (
          <SpreadsheetCommentPopover
            mountId={mountId}
            path={path}
            rangeLabel={commentPopoverSelection.rangeLabel || commentPopoverSelection.sheetName || 'Selection'}
            onCancel={cancelCommentPopover}
            onSave={saveCommentPopover}
            onValidationError={onReviewError}
          />
        ) : null}
      </FileReviewWorkspace>
      <DiscussionNotificationPrompt enabled={discussionsEnabled && Boolean(mountId && path)} />
      <input
        ref={fileInputRef}
        type="file"
        accept={fileInputAccept}
        hidden
        onChange={(event) => void onFileInputChange(event)}
      />
      <DocumentOpenPickerPopup
        visible={pickerOpen}
        kind="spreadsheet"
        onClose={() => setPickerOpen(false)}
        onOpen={onPickerOpen}
      />
    </div>
  );
}
